import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';

const NAV_LINKS = [
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'projects', label: 'Projects' },
];

const SOCIAL_LINKS = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/bishal-gautam-2428911a6/' },
  { label: 'Email', href: 'mailto:bshalgautam12@gmail.com' },
  { label: 'GitHub', href: 'https://github.com/Bishal2k' },
];

export default function Sidebar() {
  const [activeSection, setActiveSection] = useState('about');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const scrollRoot = document.getElementById('main-scroll');
    const isScrollable = scrollRoot != null && scrollRoot.scrollHeight > scrollRoot.clientHeight;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { root: isScrollable ? scrollRoot : null, rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    NAV_LINKS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const mainScroll = document.getElementById('main-scroll');
    const isDesktop = mainScroll != null && mainScroll.scrollHeight > mainScroll.clientHeight;
    if (isDesktop) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      const sidebarHeight = document.querySelector('aside')?.offsetHeight ?? 0;
      const top = el.getBoundingClientRect().top + window.pageYOffset - sidebarHeight - 8;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <Box
      component="aside"
      sx={{
        flex: '0 0 320px',
        width: 320,
        bgcolor: '#0A0E2B',
        borderRight: '1px solid rgba(111,243,232,0.12)',
        padding: '56px 40px',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
        overflowY: 'auto',
        boxSizing: 'border-box',
        '@media (max-width: 768px)': {
          position: 'sticky',
          top: 0,
          zIndex: 100,
          flex: '0 0 auto',
          width: '100%',
          borderRight: 'none',
          borderBottom: '1px solid rgba(111,243,232,0.12)',
          padding: '14px 24px 0',
          gap: '4px',
          overflowY: 'visible',
        },
      }}
    >
      {/* Name + Title */}
      <Box>
        <Typography
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontWeight: 600,
            fontSize: '30px',
            color: '#EDEFF7',
            lineHeight: 1.15,
            '@media (max-width: 768px)': {
              fontSize: '22px',
            },
          }}
        >
          Bishal Gautam
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontWeight: 500,
            fontSize: '16px',
            color: '#6FF3E8',
            mt: 1,
            '@media (max-width: 768px)': {
              fontSize: '13px',
              mt: '2px',
            },
          }}
        >
          Full Stack Developer
        </Typography>
      </Box>

      {/* Nav links */}
      <Stack
        gap="4px"
        sx={{
          '@media (max-width: 768px)': {
            flexDirection: 'row',
            gap: 0,
            mt: '4px',
          },
        }}
      >
        {NAV_LINKS.map(({ id, label }) => {
          const isActive = activeSection === id;
          return (
            <Box
              key={id}
              component="a"
              href={`#${id}`}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                scrollTo(id);
              }}
              sx={{
                color: isActive ? '#6FF3E8' : '#A8BEF0',
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '14px',
                fontWeight: isActive ? 500 : 400,
                padding: '8px 0 8px 14px',
                borderLeft: isActive ? '2px solid #6FF3E8' : '2px solid transparent',
                textDecoration: 'none',
                transition: 'color 0.15s, border-color 0.15s',
                cursor: 'pointer',
                '&:hover': { color: '#6FF3E8' },
                '@media (max-width: 768px)': {
                  padding: '6px 14px 8px',
                  borderLeft: 'none',
                  borderBottom: isActive ? '2px solid #6FF3E8' : '2px solid transparent',
                  fontSize: '13px',
                },
              }}
            >
              {label}
            </Box>
          );
        })}
      </Stack>

      {/* Social links — pushed to bottom on desktop, hidden on mobile */}
      <Stack gap="10px" sx={{ mt: 'auto', '@media (max-width: 768px)': { display: 'none' } }}>
        {SOCIAL_LINKS.map(({ label, href }) => (
          <Box
            key={label}
            component="a"
            href={href}
            target={href.startsWith('http') ? '_blank' : undefined}
            rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
            sx={{
              color: '#A8BEF0',
              fontSize: '13px',
              textDecoration: 'none',
              display: 'flex',
              justifyContent: 'space-between',
              '&:hover': { color: '#6FF3E8' },
              transition: 'color 0.15s',
            }}
          >
            {label} <span>↗</span>
          </Box>
        ))}
      </Stack>

      {/* Copyright — hidden on mobile */}
      <Typography sx={{ color: '#3E4670', fontSize: '12px', '@media (max-width: 768px)': { display: 'none' } }}>
        © 2026 Bishal Gautam
      </Typography>
    </Box>
  );
}
